var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum, ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { Text } from '@inter-ikea-kompis/component-text';
import { TextToggle } from '@inter-ikea-kompis/component-text-toggle';
import EprelQrToggle from '../eprel-qr-toggle/EprelQrToggle.js';
import EnergyLabelCardStyle from './EnergyLabelCardStyle.js';
const LABEL_FILE_TYPE = 'svg';
/**
 * Data-driven information listing.
 */
export default class EnergyLabelCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Filter the energy labels to be shown. By default, all labels are visible.
        this.labelFilter = null;
        this.expandedTextToggle = false;
    }
    /**
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        const rescaled = !!ProductUtility.getProductInformationSheetLink(this.product);
        const energyType = ProductUtility.getProductComplianceType(this.product);
        return html `
			<style>
				${EnergyLabelCardStyle.getStyle({
            theme: this.theme,
            rescaled,
            energyType
        })}
			</style>
			<div class="container">${this.getCard()}</div>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-qr-code', QrCode);
        this.customElements.define('kompis-text-toggle', TextToggle);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-eprel-qr-toggle', EprelQrToggle);
        super.connectedCallback();
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    /**
     * Returns either Eprel card or Fiche card depending on what data comes from DEXF.
     */
    getCard() {
        return html `
			<div class="labels">${this.getLabels()}</div>
			${this.getEprelLink()}
		`;
    }
    getLabels() {
        return this.getLabelUrls().map((url) => html `<img src="${url}" alt="${this.getImageAlt()}" />`);
    }
    /**
     * Return url to the label SVGs.
     */
    getLabelUrls() {
        if (this.product.content?.complianceLabel?.length) {
            return this.product.content.complianceLabel
                .filter((label) => {
                if (this.labelFilter) {
                    return this.labelFilter.includes(label);
                }
                return true;
            })
                .filter((label) => label.fileType === LABEL_FILE_TYPE)
                .map((label) => label.url);
        }
        return [];
    }
    /**
     * Returns corresponding alt text for the energy efficiency class image.
     */
    getImageAlt() {
        const complianceLabelType = ProductUtility.getProductComplianceType(this.product);
        switch (complianceLabelType) {
            case ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass: {
                const eecObject = this.product?.content?.complianceTechnical?.find((obj) => obj.headingType === 'ENERGY_EFFICIENCY_CLASS');
                if (!eecObject) {
                    return '';
                }
                const { headingText, text } = eecObject;
                return `${headingText} ${text}`;
            }
            case ProductComplianceTechnicalHeadingTypeEnum.hyperlink: {
                return this.product?.content?.complianceLabel?.find((obj) => obj.labelType)?.typeName ?? '';
            }
            case ProductComplianceTechnicalHeadingTypeEnum.nonRescaled: {
                const eecObject = this.product?.content?.technicalInformation?.find((obj) => obj.typeCode === '00026');
                if (!eecObject) {
                    return '';
                }
                const { typeName, valueMetric } = eecObject;
                return `${typeName} ${valueMetric}`;
            }
            default: {
                return '';
            }
        }
    }
    /**
     * Get EPREL link.
     */
    getEprelLink() {
        const link = this.product ? ProductUtility.getProductInformationSheetLink(this.product) : null;
        if (!link) {
            return null;
        }
        if (this.disableLinks) {
            return html `<kompis-eprel-qr-toggle
				.theme="${this.theme}"
				.product="${this.product}"
				.expanded="${this.expandedTextToggle}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				@animationEnd="${(event) => {
                if (this.expandedTextToggle) {
                    event.target.scrollIntoView({
                        behavior: 'smooth'
                    });
                }
            }}"
				@expand="${(event) => {
                this.expandedTextToggle = event.detail.expanded;
                this.update();
            }}"
			></kompis-eprel-qr-toggle>`;
        }
        return html ` <kompis-text
			class="eprelLink"
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.bodySmall}"
		>
			<kompis-hyperlink
				.target="${'_blank'}"
				.href="${link.href}"
				.theme="${this.theme}"
				.color="${HyperlinkColorEnum.grey}"
				.text="${link.text}"
				.underline="${true}"
			></kompis-hyperlink>
		</kompis-text>`;
    }
}
__decorate([
    property({ type: Object, required: true })
], EnergyLabelCard.prototype, "product", void 0);
__decorate([
    property({ type: Object, required: true })
], EnergyLabelCard.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], EnergyLabelCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], EnergyLabelCard.prototype, "translations", void 0);
__decorate([
    property({ type: Boolean })
], EnergyLabelCard.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Object })
], EnergyLabelCard.prototype, "labelFilter", void 0);
//# sourceMappingURL=EnergyLabelCard.js.map